import { ItemStatus } from '../domain/models/db-order';
import { Role } from '@pcmp-checka/lib-utilities/atkomst';

/**
 * Context passed to transition conditions for evaluation
 */
export interface TransitionContext {
  orderType?: string;
}

// Item status state machine
// This object must be synchronized with the definition of the diagram located in "../../../../docs/assets/items-status.png"
export const ITEM_STATUS_MACHINE: Record<
  ItemStatus,
  {
    status: ItemStatus;
    evaluate: (context: TransitionContext) => ItemStatus[]; // Evaluate next states based on context
    updateRole: Role[]; // The roles required (one of) to update to this status
    updateByManager: boolean; // The status can be updated by the user manager, even though not in updateRole
    reasons: string[]; // Possible reasons for this status
  }
> = {
  [ItemStatus.Pending]: {
    status: ItemStatus.Pending,
    evaluate: (context) => {
      const baseNext = [ItemStatus.Approved, ItemStatus.Rejected];
      // Inventory orders can be directly collected when approved, skipping the prepared state
      if (context.orderType === 'inventory') {
        return [...baseNext, ItemStatus.Collected];
      }
      return baseNext;
    },
    updateRole: [], // Setted automatically when creating the order item
    updateByManager: false,
    reasons: [],
  },
  [ItemStatus.Rejected]: {
    status: ItemStatus.Rejected,
    evaluate: () => [],
    updateRole: [
      'CHECKA Order Admin', // "Order Admin"
    ],
    updateByManager: true,
    reasons: [],
  },
  [ItemStatus.Approved]: {
    status: ItemStatus.Approved,
    evaluate: () => [ItemStatus.Prepared, ItemStatus.Cancelled],
    updateRole: [
      'CHECKA Order Admin', // "Order Admin"
    ],
    updateByManager: true,
    reasons: [],
  },
  [ItemStatus.Prepared]: {
    status: ItemStatus.Prepared,
    evaluate: () => [ItemStatus.Collected, ItemStatus.Cancelled],
    updateRole: [
      'CHECKA Order Admin', // "Order Admin"
    ],
    updateByManager: false,
    reasons: ['pickup', 'handoff'],
  },
  [ItemStatus.Cancelled]: {
    status: ItemStatus.Cancelled,
    evaluate: () => [],
    updateRole: [
      'CHECKA Order Admin', // "Order Admin"
      'CHECKA Location Viewer', // "Location Viewer"
    ],
    updateByManager: false,
    reasons: [],
  },
  [ItemStatus.Collected]: {
    status: ItemStatus.Collected,
    evaluate: () => [ItemStatus.Discarded, ItemStatus.Recalled],
    updateRole: [
      'CHECKA Order Admin', // "Order Admin"
      'CHECKA Location Viewer', // "Location Viewer"
    ],
    updateByManager: false,
    reasons: [],
  },
  [ItemStatus.Discarded]: {
    status: ItemStatus.Discarded,
    evaluate: () => [],
    updateRole: [
      'CHECKA Order Admin', // "Order Admin"
    ],
    updateByManager: false,
    reasons: ['discarded', 'lost', 'giveaway'],
  },
  [ItemStatus.Recalled]: {
    status: ItemStatus.Recalled,
    evaluate: () => [
      ItemStatus.Discarded,
      ItemStatus.Returned,
      ItemStatus.Refurbish,
    ],
    updateRole: [
      'CHECKA Order Admin', // "Order Admin"
      'CHECKA Location Viewer', // "Location Viewer"
    ],
    updateByManager: false,
    reasons: [],
  },
  [ItemStatus.Returned]: {
    status: ItemStatus.Returned,
    evaluate: () => [],
    updateRole: [
      'CHECKA Order Admin', // "Order Admin"
    ],
    updateByManager: false,
    reasons: [],
  },
  [ItemStatus.Refurbish]: {
    status: ItemStatus.Refurbish,
    evaluate: () => [ItemStatus.Discarded, ItemStatus.Returned],
    updateRole: [
      'CHECKA Order Admin', // "Order Admin"
    ],
    updateByManager: false,
    reasons: ['damaged', 'recycle'],
  },
};

export const getItemStatus = (status: ItemStatus) =>
  ITEM_STATUS_MACHINE[status];

export const getNextItemStatuses = (
  status: ItemStatus,
  context: TransitionContext = {}
) => getItemStatus(status).evaluate(context);

/**
 * Validates if a status transition is valid based on context
 * @param currentStatus - The current item status
 * @param newStatus - The new status to transition to
 * @param context - Context for evaluating transitions
 * @returns true if the transition is valid, false otherwise
 */
export const isValidStatusTransition = (
  currentStatus: ItemStatus,
  newStatus: ItemStatus,
  context: TransitionContext = {}
): boolean => {
  const nextStatuses = getNextItemStatuses(currentStatus, context);
  return nextStatuses.indexOf(newStatus) !== -1;
};
